import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@babackapp/ui/components/avatar";
import { Button } from "@babackapp/ui/components/button";
import Loader from "@babackapp/ui/components/loader";
import { cn } from "@babackapp/ui/lib/utils";
import { Pencil2Icon, TrashIcon } from "@radix-ui/react-icons";
import { useCallback, useRef, useState } from "react";
import FormInputContainer from "./form-input-container";

type FormEditableAvatarProps = {
  label: string;
  description?: string;
  initialValue: string | null | undefined;
  // onChange: (f: { id: string; url: string }) => void;
  onRemove: () => void;
  isLoading: boolean;
  fallback?: string;
  disabled?: boolean | string;
  onUpload: (file: File) => unknown;
};

export default function FormEditableAvatar({
  label,
  description,
  initialValue,
  fallback,
  onRemove,
  isLoading,
  onUpload,
  disabled,
  // onChange
}: FormEditableAvatarProps) {
  const [url, setUrl] = useState<string | null | undefined>(initialValue);
  const fileInput = useRef<HTMLInputElement>(null);

  const _onChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const file = e.target.files?.[0];
      if (!file) return;
      onUpload(file);
      setUrl(URL.createObjectURL(file));
      // onChange({ id, url: URL.createObjectURL(file) });
    },
    [disabled, onUpload],
    // [onChange]
  );

  const onOpenFileSelector = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      fileInput.current?.click();
    },
    [disabled],
  );

  return (
    <FormInputContainer
      direction="horizontal"
      label={label}
      description={description}
      disabled={disabled}
    >
      <div className="relative h-16 w-16 rounded-full group bg-muted">
        <Avatar
          className={cn(
            "h-16 w-16 z-10",
            isLoading && "opacity-50 pointer-events-none",
          )}
        >
          <AvatarImage src={url ?? undefined} />
          {fallback && (
            <AvatarFallback>{fallback.charAt(0).toUpperCase()}</AvatarFallback>
          )}
        </Avatar>
        {url && !isLoading ? (
          <Button
            variant="secondary"
            role="button"
            type="button"
            size="sm"
            className="absolute -bottom-1 z-30 -right-1 p-0 w-6 h-6 flex items-center justify-center rounded-full"
            onClick={() => {
              onRemove();
              setUrl(null);
            }}
          >
            <TrashIcon className="h-4 w-4 text-secondary-foreground" />
          </Button>
        ) : null}
        <Button
          role="button"
          type="button"
          variant="ghost"
          className="absolute z-20 inset-0 h-full w-full bg-black bg-opacity-50 flex items-center rounded-full justify-center opacity-0 hover:opacity-80 transition-all hover:bg-black"
          onClick={onOpenFileSelector}
        >
          <Pencil2Icon className="h-6 w-6 text-white" />
        </Button>
        {isLoading && (
          <div className="absolute inset-0 rounded-full z-30 pointer-events-none flex items-center justify-center">
            <Loader className="w-4 h-4" />
          </div>
        )}
      </div>

      <input
        className="hidden"
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={_onChange}
      />
    </FormInputContainer>
  );
}
